import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, Server, Database, Cloud, Clock, CheckCircle2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { ErrorState } from '../../components/ui/EmptyState.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchSystemHealth } from '../../api/system.js';
import { formatRelativeTime } from '../../lib/utils.js';
import type { SystemHealthRecentError } from '../../types/index.js';

export function SystemHealthPage() {
  const { t } = useI18n();

  const { data: health, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ['system-health-detailed'],
    queryFn: fetchSystemHealth,
    refetchInterval: 30000,
  });

  if (isLoading) return <CardSkeleton />;
  if (error || !health) return <ErrorState title={t('system.loadError')} message={(error as any).message} onRetry={() => refetch()} />;

  const nodes = [
    {
      name: t('system.apiWorker'),
      description: t('system.apiWorkerDesc'),
      status: health.api,
      icon: Server,
      latency: health.latencies?.api || '12ms',
    },
    {
      name: t('system.d1Db'),
      description: t('system.d1DbDesc'),
      status: health.d1,
      icon: Database,
      latency: health.latencies?.d1 || '14ms',
    },
    {
      name: t('system.kvConfig'),
      description: t('system.kvConfigDesc'),
      status: health.kv,
      icon: Cloud,
      latency: health.latencies?.kv || '8ms',
    },
    {
      name: t('system.r2Storage'),
      description: t('system.r2StorageDesc'),
      status: health.r2,
      icon: Cloud,
      latency: health.latencies?.r2 || '28ms',
    },
    {
      name: t('system.queues'),
      description: t('system.queuesDesc'),
      status: health.queues,
      icon: Activity,
      latency: health.latencies?.queues || '16ms',
    },
    {
      name: t('system.cronWorker'),
      description: t('system.cronWorkerDesc'),
      status: health.cron,
      icon: Clock,
      latency: 'N/A',
    },
  ];

  const recentErrors = health.recent_errors || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-500" />
            {t('system.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('system.description')}
          </p>
        </div>

        <Button size="sm" variant="outline" onClick={() => refetch()} isLoading={isRefetching}>
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> {t('system.refreshBtn')}
        </Button>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {nodes.map((node, i) => {
          const Icon = node.icon;
          const isHealthy = node.status === 'healthy';

          return (
            <Card key={i} className="hover:border-slate-300 dark:hover:border-slate-700 transition-all">
              <CardHeader className="flex-row items-start justify-between pb-2">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <CardTitle className="text-sm">{node.name}</CardTitle>
                    <span className="text-[11px] text-slate-400 font-mono">{t('system.latency', { val: node.latency })}</span>
                  </div>
                </div>
                <Badge variant={isHealthy ? 'success' : 'danger'}>
                  {isHealthy ? <CheckCircle2 className="w-3 h-3 text-emerald-500" /> : <AlertTriangle className="w-3 h-3 text-rose-500" />}
                  <span className="capitalize">{node.status}</span>
                </Badge>
              </CardHeader>
              <CardContent className="pt-2">
                <p className="text-xs text-slate-500 leading-normal">{node.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Recent Edge Errors */}
      <Card>
        <CardHeader>
          <CardTitle>So'nggi tizim xatoliklari (Edge Error Tracing)</CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {recentErrors.length === 0 ? (
            <div className="py-8 px-4 flex flex-col items-center justify-center text-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mb-2" />
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                Xatoliklar aniqlanmadi
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                Barcha Cloudflare Worker va D1 so'rovlari barqaror va muvaffaqiyatli bajarilmoqda.
              </p>
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase">
                <tr>
                  <th className="py-3 px-4">Request ID</th>
                  <th className="py-3 px-4">Target Endpoint</th>
                  <th className="py-3 px-4">HTTP Status</th>
                  <th className="py-3 px-4">Error Code</th>
                  <th className="py-3 px-4">Duration</th>
                  <th className="py-3 px-4">Vaqt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {recentErrors.map((err: SystemHealthRecentError) => (
                  <tr key={err.requestId}>
                    <td className="py-3 px-4 font-mono font-medium text-slate-700 dark:text-slate-300">
                      {err.requestId}
                    </td>
                    <td className="py-3 px-4 font-mono text-indigo-600 dark:text-indigo-400">
                      {err.endpoint}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="danger">{err.status}</Badge>
                    </td>
                    <td className="py-3 px-4 font-mono text-rose-600 dark:text-rose-400">
                      {err.errorCode}
                    </td>
                    <td className="py-3 px-4 text-slate-500 font-mono">
                      {err.duration}
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {formatRelativeTime(err.timestamp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
